import { useCallback } from "react";

export const useAnalytics = () => {
  const trackEvent = useCallback((eventName, parameters = {}) => {
    if (window.gtag) {
      window.gtag("event", eventName, parameters);
    }
  }, []);

  const trackPageView = useCallback((pagePath, pageTitle) => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: pagePath,
        page_title: pageTitle || document.title,
      });
    }
  }, []);

  // Player tracking events
  const trackPlayerSelected = useCallback(
    (playerId, playerName, slotIndex, source) => {
      trackEvent("player_selected", {
        player_id: playerId,
        player_name: playerName,
        slot_index: slotIndex,
        source,
      });
    },
    [trackEvent]
  );

  const trackPlayerViewed = useCallback(
    (playerId, playerName, source) => {
      trackEvent("player_viewed", {
        player_id: playerId,
        player_name: playerName,
        source,
      });
    },
    [trackEvent]
  );

  const trackComparePlayers = useCallback(
    (playerCount, players, stat) => {
      trackEvent("compare_players", {
        player_count: playerCount,
        players: players.join(", "),
        stat,
      });
    },
    [trackEvent]
  );

  // Widget tracking events
  const trackWidgetSaved = useCallback(
    (widgetType, chartType, stat, playerCount, playerId, playerName) => {
      const params = {
        widget_type: widgetType,
        chart_type: chartType,
        stat,
      };

      if (playerCount !== undefined) params.player_count = playerCount;
      if (playerId) params.player_id = playerId;
      if (playerName) params.player_name = playerName;

      trackEvent("widget_saved", params);
    },
    [trackEvent]
  );

  const trackWidgetDeleted = useCallback(
    (widgetType, widgetTitle, dashboardType) => {
      trackEvent("widget_deleted", {
        widget_type: widgetType || "unknown",
        widget_title: widgetTitle || "untitled",
        dashboard_type: dashboardType,
      });
    },
    [trackEvent]
  );

  const trackWidgetCreationStarted = useCallback(
    (source, dashboardType) => {
      trackEvent("widget_creation_started", {
        source,
        dashboard_type: dashboardType,
      });
    },
    [trackEvent]
  );

  const trackWidgetCreationOptionSelected = useCallback(
    (option, source) => {
      trackEvent("widget_creation_option_selected", {
        option,
        source,
      });
    },
    [trackEvent]
  );

  // Dashboard tracking events
  const trackDashboardEditModeEntered = useCallback(
    (dashboardType, widgetCount) => {
      trackEvent("dashboard_edit_mode_entered", {
        dashboard_type: dashboardType,
        widget_count: widgetCount,
      });
    },
    [trackEvent]
  );

  const trackPhantomCardClicked = useCallback(
    (dashboardType, source) => {
      trackEvent("phantom_card_clicked", {
        dashboard_type: dashboardType,
        source,
      });
    },
    [trackEvent]
  );

  // Authentication tracking events
  const trackLogin = useCallback(
    (method, source, success = true) => {
      trackEvent("login", {
        method,
        source,
        success,
      });
    },
    [trackEvent]
  );

  const trackSignup = useCallback(
    (method, success = true) => {
      trackEvent("signup", {
        method,
        success,
      });
    },
    [trackEvent]
  );

  // Generic event tracking
  const trackCustomEvent = useCallback(
    (eventName, parameters) => {
      trackEvent(eventName, parameters);
    },
    [trackEvent]
  );

  const trackSimilarSeasonsViewed = useCallback(
    ({ league, playerId, season, role, resultCount }) => {
      const params = {
        league,
        player_id: playerId,
        season,
        result_count: resultCount,
      };
      if (role) params.role = role;
      trackEvent("similar_seasons_viewed", params);
    },
    [trackEvent]
  );

  const trackSimilarSeasonClicked = useCallback(
    ({
      league,
      role,
      sourcePlayerId,
      sourceSeason,
      destPlayerId,
      destSeason,
      resultRank,
    }) => {
      const params = {
        league,
        source_player_id: sourcePlayerId,
        source_season: sourceSeason,
        dest_player_id: destPlayerId,
        dest_season: destSeason,
        result_rank: resultRank,
      };
      if (role) params.role = role;
      trackEvent("similar_season_clicked", params);
    },
    [trackEvent]
  );

  return {
    // Core tracking functions
    trackEvent,
    trackPageView,

    // Player tracking
    trackPlayerSelected,
    trackPlayerViewed,
    trackComparePlayers,

    // Widget tracking
    trackWidgetSaved,
    trackWidgetDeleted,
    trackWidgetCreationStarted,
    trackWidgetCreationOptionSelected,

    // Dashboard tracking
    trackDashboardEditModeEntered,
    trackPhantomCardClicked,

    // Authentication tracking
    trackLogin,
    trackSignup,

    // Generic tracking
    trackCustomEvent,

    // Similar seasons
    trackSimilarSeasonsViewed,
    trackSimilarSeasonClicked,
  };
};
